"use client";

import React, { useEffect, useRef, useState } from "react";
import { FlaskConical, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const milestones = [{
  year: "July 2023",
  title: "Strategic Foundation",
  description: "Incorporated in Swan Hill, Victoria, establishing BRENDON J BUILDING MATERIAL WHOLESALERS PTY LTD as a dedicated B2B supply partner."
}, {
  year: "Late 2023",
  title: "Supply Chain Development",
  description: "Formalized bulk procurement contracts for structural steel and high-grade cement, ensuring inventory stability for regional Victorian builders."
}, {
  year: "Early 2024",
  title: "Logistics Fleet Launch",
  description: "Integrated heavy-duty delivery systems to facilitate site-direct distribution, streamlining the procurement cycle for our construction partners."
}, {
  year: "Present",
  title: "Full-Scale Wholesale Hub",
  description: "Expanded inventory to 5,000+ SKUs across electrical, plumbing, and hardware, serving as a single-source supplier for major infrastructure projects."
}];
function MilestoneCard({
  milestone,
  index,
  "data-index": dataIndex
}: {
  milestone: typeof milestones[0];
  index: number;
  "data-index": number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="152656" ref={ref} data-index={dataIndex} className="relative flex items-start w-full mb-16 last:mb-0 group">
      {}
      <div className={cn("absolute left-4 md:left-1/2 w-4 h-4 rounded-full border-2 -translate-x-1/2 mt-7 z-10 transition-all duration-700", isVisible ? "bg-primary border-primary shadow-md scale-110" : "bg-background border-muted-foreground/30 scale-100")} />

      {}
      <div className={cn("w-full pl-12 md:pl-0 md:w-1/2 transition-all duration-700 ease-out", isEven ? "md:pr-16" : "md:pl-16 md:ml-auto", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
        <Card className="border-l-4 border-l-primary shadow-sm bg-card hover:bg-accent/5 transition-colors duration-300 rounded-sm">
          <CardHeader className="pb-3">
            <Badge className="w-fit mb-4 text-xs font-mono px-3 py-1 bg-amber-500 text-primary-foreground hover:bg-amber-600 rounded-sm">
              {milestone.year}
            </Badge>
            <CardTitle className="text-xl font-serif font-bold tracking-tight text-foreground uppercase">
              {milestone.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground leading-relaxed font-sans">
              {milestone.description}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function QualityMilestones() {
  const [fillHeight, setFillHeight] = useState(0);
  const timelineRef = useRef<HTMLDivElement>(null);
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const startPoint = windowHeight * 0.66;
      const endPoint = windowHeight * 0.33;
      const totalScrollDistance = rect.height + (startPoint - endPoint);
      const currentScroll = startPoint - rect.top;
      let progress = currentScroll / totalScrollDistance;
      progress = Math.max(0, Math.min(1, progress));
      setFillHeight(progress * 100);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="milestones" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div ref={headerRef} className={cn("text-center max-w-3xl mx-auto mb-20 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="inline-flex items-center justify-center p-3 bg-amber-500/10 rounded-sm mb-6">
            <ShieldCheck className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-3xl md:text-5xl font-serif font-extrabold mb-6 tracking-tighter uppercase text-primary">
            Wholesale Growth Timeline
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Tracking our evolution from a newly incorporated local supplier to a premier wholesale partner for the Australian building industry.
          </p>
        </div>

        {}
        <div className="relative max-w-5xl mx-auto" ref={timelineRef}>
          {}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-border/50 -translate-x-1/2 rounded-full" />
          
          {}
          <div className="absolute left-4 md:left-1/2 top-0 w-0.5 bg-amber-500 -translate-x-1/2 rounded-full transition-all duration-150 ease-out shadow-sm" style={{
          height: `${fillHeight}%`
        }} />

          {}
          <div className="relative z-10 py-4">
            {milestones.map((milestone, index) => <MilestoneCard key={index} data-index={index} milestone={milestone} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}